/**
 * Core Stepper Component
 *
 * Multi-step wizard with horizontal/vertical orientation, step status,
 * clickable steps, optional/error states, and icon customization.
 *
 * @example
 *   <Stepper
 *     steps={[
 *       { id: 'details', label: 'Details' },
 *       { id: 'config', label: 'Configuration', optional: true },
 *       { id: 'review', label: 'Review & Submit' },
 *     ]}
 *     activeStep={1}
 *     onStepClick={setStep}
 *   />
 */
import React from 'react';
import {
  Stepper as MuiStepper,
  Step as MuiStep,
  StepLabel,
  StepConnector,
  Typography,
} from '@mui/material';
import { Check, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface StepItem {
  id: string;
  label: string;
  /** Subtitle / description */
  description?: string;
  /** Mark as optional step */
  optional?: boolean;
  /** Mark step as having an error */
  error?: boolean;
  /** Custom icon */
  icon?: React.ReactNode;
  /** Disable clicking this step */
  disabled?: boolean;
}

export interface StepperProps {
  steps: StepItem[];
  /** Active step index (0-based) */
  activeStep: number;
  /** Step click handler — makes steps clickable */
  onStepClick?: (index: number) => void;
  /** Orientation */
  orientation?: 'horizontal' | 'vertical';
  /** Alternative label placement (below icon) — horizontal only */
  alternativeLabel?: boolean;
  /** Linear mode — can only go forward */
  linear?: boolean;
  /** Completed steps (override) */
  completedSteps?: number[];
  /** Size */
  size?: 'sm' | 'md';
  className?: string;
}

export const Stepper: React.FC<StepperProps> = ({
  steps,
  activeStep,
  onStepClick,
  orientation = 'horizontal',
  alternativeLabel = false,
  linear = false,
  completedSteps,
  size = 'md',
  className = '',
}) => {
  const isCompleted = (idx: number) => {
    if (completedSteps) return completedSteps.includes(idx);
    return idx < activeStep;
  };

  return (
    <MuiStepper
      activeStep={activeStep}
      orientation={orientation}
      alternativeLabel={alternativeLabel}
      nonLinear={!linear}
      className={className}
      sx={{
        '& .MuiStepLabel-label': {
          fontSize: size === 'sm' ? '0.75rem' : '0.8125rem',
          fontWeight: 600,
        },
      }}
    >
      {steps.map((step, idx) => (
        <MuiStep
          key={step.id}
          completed={isCompleted(idx)}
          disabled={step.disabled}
        >
          <StepLabel
            error={step.error}
            optional={
              step.optional ? (
                <Typography variant="caption" color="text.secondary">Optional</Typography>
              ) : step.description ? (
                <Typography variant="caption" color="text.secondary">{step.description}</Typography>
              ) : undefined
            }
            icon={step.error ? <AlertCircle className="h-5 w-5 text-red-500" /> : step.icon}
            onClick={onStepClick && !step.disabled ? () => onStepClick(idx) : undefined}
            sx={{
              cursor: onStepClick && !step.disabled ? 'pointer' : 'default',
              '&:hover': onStepClick && !step.disabled ? { opacity: 0.8 } : {},
            }}
          >
            {step.label}
          </StepLabel>
        </MuiStep>
      ))}
    </MuiStepper>
  );
};
export default Stepper;
